import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { authApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { hasPermission, type Permission } from '../lib/permissions.js';

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 認証フック。auth.me oRPC procedure でセッション検証・login/logoutミューテーション・権限チェック（can関数）を統合提供
 * @graph-connects /rpc [calls] auth.me / auth.logout oRPC procedures
 * @graph-connects authApi [calls] 認証APIクライアント経由でログイン・ログアウト・セッション取得
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 * @graph-connects hasPermission [calls] 権限チェックヘルパー呼び出し
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.auth.current,
    queryFn: authApi.getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: () => {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
      return Promise.resolve();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.auth.current, null);
      window.location.href = '/login';
    },
  });

  const user = data?.user ?? null;

  // 権限チェックヘルパー（ロール + チーム条件対応）
  const can = (permission: Permission): boolean => {
    return hasPermission(user?.portalRole, permission, user?.teamName);
  };

  return {
    user,
    expiresAt: data?.expiresAt,
    isAuthenticated: !!user,
    isLoading,
    error,
    refetch,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    can,
  };
}
